import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { VERANSTALTUNGSSTATUS_BESCHRIFTUNG, istAktiv } from '@mcikm-vote/shared';
import type { Veranstaltung } from '@mcikm-vote/shared';

import { useVeranstaltungen } from '../../admin/veranstaltungenApi';
import { formatiereDatum } from '../../formatierung';

/** Farbliche **und** textliche Unterscheidung — §21 verbietet rein farbbasierte Information. */
function StatusZeichen({ status }: { status: Veranstaltung['status'] }): ReactElement {
  const farbe =
    status === 'laufend'
      ? 'bg-erfolg/15 text-erfolg'
      : status === 'freigegeben'
        ? 'bg-akzent/15 text-akzent'
        : 'bg-flaeche-gedaempft text-text-gedaempft';

  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${farbe}`}>
      {VERANSTALTUNGSSTATUS_BESCHRIFTUNG[status]}
    </span>
  );
}

/**
 * Übersicht der Veranstaltungen (§6).
 *
 * Archivierte Veranstaltungen sind nach §6.2 aus der normalen Übersicht ausgeblendet und lassen
 * sich ausdrücklich einblenden.
 */
export function VeranstaltungenSeite(): ReactElement {
  const [mitArchivierten, setMitArchivierten] = useState(false);
  const veranstaltungen = useVeranstaltungen(mitArchivierten);

  const aktive = veranstaltungen.data?.find((v) => istAktiv(v.status));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Veranstaltungen</h1>
        <Link
          to="neu"
          className="bg-akzent text-akzent-text transition hover:brightness-110 min-h-touchflaeche flex items-center rounded-md px-4 font-medium"
        >
          Neue Veranstaltung
        </Link>
      </div>

      <p className="text-text-gedaempft text-sm">
        {aktive === undefined
          ? 'Derzeit ist keine Veranstaltung freigegeben oder laufend.'
          : `Aktiv: ${aktive.bezeichnung} (Code ${aktive.code}).`}{' '}
        Systemweit darf immer nur eine Veranstaltung aktiv sein.
      </p>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={mitArchivierten}
          onChange={(ereignis) => {
            setMitArchivierten(ereignis.target.checked);
          }}
        />
        Archivierte anzeigen
      </label>

      {veranstaltungen.isPending && <p className="text-text-gedaempft text-sm">Wird geladen …</p>}

      {veranstaltungen.isError && (
        <p role="alert" className="text-fehler text-sm font-medium">
          {veranstaltungen.error.message}
        </p>
      )}

      {veranstaltungen.data?.length === 0 && (
        <p className="border-rand bg-flaeche text-text-gedaempft rounded-xl border p-4 text-sm">
          Es ist noch keine Veranstaltung angelegt.
        </p>
      )}

      {veranstaltungen.data !== undefined && veranstaltungen.data.length > 0 && (
        <div className="border-rand bg-flaeche overflow-x-auto rounded-xl border shadow-sm">
          <table className="w-full text-sm">
            <thead className="border-rand text-text-gedaempft border-b text-left">
              <tr>
                <th className="p-3 font-medium">Bezeichnung</th>
                <th className="p-3 font-medium">Datum</th>
                <th className="p-3 font-medium">Code</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium">Tokens</th>
              </tr>
            </thead>
            <tbody>
              {veranstaltungen.data.map((v) => (
                <tr key={v.id} className="border-rand border-b last:border-b-0">
                  <td className="p-3">
                    <Link to={v.id} className="hover:text-akzent font-medium underline">
                      {v.bezeichnung}
                    </Link>
                    {v.ort !== null && (
                      <span className="text-text-gedaempft block text-xs">{v.ort}</span>
                    )}
                  </td>
                  <td className="p-3">{formatiereDatum(v.veranstaltungsdatum)}</td>
                  <td className="p-3 font-mono">{v.code}</td>
                  <td className="p-3">
                    <StatusZeichen status={v.status} />
                  </td>
                  <td className="p-3">
                    {v.tokensGueltig} gültig
                    <span className="text-text-gedaempft"> von {v.tokensGesamt}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
